import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { useEmbeddedContext } from '@src/hooks';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useNavigateToTradingOrderDetails } from '@src/hooks/useNavigateToTradingOrderDetails';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import {
  BlotterTable,
  BlotterTableFilters,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportDialog,
  MaxRecordsReachedWarning,
  MixpanelEvent,
  SummaryLine,
  createExportFileName,
  filterByColumnMainMenuItems,
  getOrderAmount,
  getOrderQuantity,
  isGridApiReady,
  useAccordionFilterBuilder,
  useBlotterTable,
  useDateRangeFilter,
  useDisclosure,
  useMixpanel,
  type ExportGridMode,
  type Order,
} from '@talos/kyoko';
import type {
  GetMainMenuItemsParams,
  RowClassParams,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-enterprise';
import { useCallback, useMemo, useState } from 'react';
import type { OrderBlotterDefaultColumn } from './orderColumnIds';
import { OrderOptions } from './OrderOptions';
import { orderTimestampFields } from './orderTimestampFields';
import { useFilteredOrdersBlotter } from './useFilteredOrdersBlotter';
import { useHandleExportOrders } from './useHandleExportOrders';
import { colIDToFilterBuilderKey, type BlotterTableOrderFilter, type UseOrderFilterOutput } from './useOrderFilter';
import { useOrderMenu } from './useOrderMenu';

interface FilteredOrdersParams {
  blotterID: string;
  tabLabel?: string;
  defaultFilter: BlotterTableOrderFilter;
  /* filters that are always applied, not visible in the UI and cannot be changed */
  permanentFilters?: BlotterTableOrderFilter;
  defaultColumns?: OrderBlotterDefaultColumn[];

  onCloneTab?: () => void;

  /** When mounting should the filter accordian be expanded */
  initialIsOpen?: boolean;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<Order>) => void;
  generateOrderDetailsRoute?: GenerateOrderDetailsRoute;

  /** Skip showing Customer columns even when enableCustomerBlotterColumns toggle is on */
  suppressCustomerColumns?: boolean;
  /** Always show DDH orders and do not show Toggle to the user */
  alwaysShowDDHOrders?: boolean;

  persistFilter?: boolean;
  /** Layout ID, currently only used for opening order details in the trading layout */
  layoutId: string | undefined;
}

export function FilteredOrders({
  blotterID,
  tabLabel,
  defaultFilter,
  permanentFilters,
  defaultColumns,
  onCloneTab,
  initialIsOpen,
  onRowDoubleClicked,
  generateOrderDetailsRoute = getTradingOrderDetailsRoute,
  suppressCustomerColumns,
  alwaysShowDDHOrders,
  persistFilter,
  layoutId,
}: FilteredOrdersParams) {
  const mixpanel = useMixpanel();
  const { homeCurrency } = useDisplaySettings();
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const { isEmbedded } = useEmbeddedContext();
  const { openDetailsDrawer } = useDetailsDrawer();

  const { filterPropsMutator, additionalFilterState } = useContextBlotterFilter<UseOrderFilterOutput>();

  const {
    density,
    rowID,
    columns,
    persisted,
    filteredOrders,
    dataObservable,
    errorObservable,
    limitReachedState,
    raisePaginationLimits,
    exportOptions,
  } = useFilteredOrdersBlotter({
    blotterID,
    tabLabel,
    defaultFilter,
    defaultColumns,
    suppressCustomerColumns,
    persistFilter,
    permanentFilters,
    additionalFilterState,
    alwaysShowDDHOrders,
  });

  const { clientSideFilter, changeFilter, filterableProperties, filterBuilderProps } = useMemo(() => {
    return filterPropsMutator(filteredOrders);
  }, [filterPropsMutator, filteredOrders]);

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Order>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const selectedRows = api.getSelectedRows();
    setSelectedOrders(selectedRows);
  }, []);

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { openClause } = filterBuilderAccordion;

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({ params, colIDToFilterBuilderKey, openClause, mixpanel });
    },
    [mixpanel, openClause]
  );

  const { getCustomContextMenuItems, dialogComponents } = useOrderMenu({
    openClause,
    filterableProperties,
    generateOrderDetailsRoute,
    layoutId,
  });

  const { showBlotterPauseButton } = useFeatureFlag();

  const onOpenDetails = useNavigateToTradingOrderDetails();

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable,
    rowID,
    density,
    clientLocalFilter: clientSideFilter,
    columns,
    persistence: persisted,
    getExtraMainMenuItems,
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: {
      // There are a few heavy valueGetters in this blotter. Enabling the value cache improves performance.
      valueCache: true,
      onSelectionChanged: handleRowSelectionChanged,
      onRowDoubleClicked(event) {
        const { data } = event;
        if (!data) {
          return;
        }
        if (onRowDoubleClicked) {
          onRowDoubleClicked(event);
        } else if (isEmbedded) {
          openDetailsDrawer({ entity: data });
        } else {
          onOpenDetails(data.OrderID);
        }
      },
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getRowStyle: getOrderRowStyle,
    },
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    exportOptions,
  });

  const dateRangeFilter = useDateRangeFilter(filteredOrders.filter, changeFilter, additionalFilterState?.DateRange);

  const handleExport = useCallback(
    (mode: ExportGridMode) => {
      const exportFunc = mode === 'CSV' ? blotterTable.exportDataAsCSV : blotterTable.exportDataAsExcel;
      exportFunc({
        fileName: createExportFileName({
          name: 'Orders',
          tabLabel,
          mode,
        }),
      });
    },
    [blotterTable.exportDataAsCSV, blotterTable.exportDataAsExcel, tabLabel]
  );

  const handleCloneTab = useCallback(() => {
    onCloneTab?.();
  }, [onCloneTab]);

  const exportDialog = useDisclosure();
  const handleExportServerData = useHandleExportOrders({ filter: filteredOrders.filter });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        timestampFields={orderTimestampFields}
        suffix={
          <OrderOptions
            changeFilter={changeFilter}
            filter={filteredOrders.filter}
            alwaysShowDDHOrders={alwaysShowDDHOrders}
            handleExport={() => {
              mixpanel.track(MixpanelEvent.ExportRowsModalOpen);
              exportDialog.open();
            }}
            handleCloneTab={onCloneTab ? handleCloneTab : undefined}
            tabType="FilteredOrders"
            blotterTable={blotterTable}
          />
        }
      />
      <MaxRecordsReachedWarning
        showLimitReached={!!limitReachedState?.lastRecord}
        earliestTimestamp={limitReachedState?.lastRecord?.Timestamp}
        recordsReceived={limitReachedState?.recordsReceived}
        onRaiseLimitClicked={raisePaginationLimits}
      />
      <BlotterTable {...blotterTable} rowEndSpacing="spacing12" />
      <SummaryLine
        homeCurrency={homeCurrency}
        rows={selectedOrders}
        getQuantity={getOrderQuantity}
        getAmount={getOrderAmount}
      />
      {dialogComponents}
      <ExportDialog
        {...exportDialog}
        handleExportLocalData={handleExport}
        handleExportServerData={handleExportServerData}
      />
    </>
  );
}

function getOrderRowStyle(params: RowClassParams<Order>) {
  if (params.data?.isArchived) {
    return { opacity: 0.5 };
  }
  return undefined;
}
